import React, { useCallback, useEffect, useState } from "react";
import { Card, Table, Button, Form, Modal, Alert, Badge, Row, Col } from "react-bootstrap";
import { FaPlus, FaPencilAlt, FaTrash } from "react-icons/fa";
import ConfirmModal from "../../common/ConfirmModal";
import { usageService } from "../../../services/usage.service";
import { isSuperUser } from "../../../utils/authUtils";
import { formatUnits, formatUnitsExact } from "../../../utils/usageUtils";

const emptyTier = {
  tierId: 0,
  tierName: "",
  includedUnits: 0,
  warnPercent: 80,
  monthlyPrice: "",
  overagePerUnit: "",
  sortOrder: 0,
  active: true,
};

const emptyAssignment = {
  clientId: "",
  tierId: 0,
  effectiveFrom: "",
  overrideLimit: "",
  notes: "",
};

/** Formats an ISO/date string as yyyy-MM-dd for a date input. */
const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

/**
 * Tier ladder and per-client assignment.
 *
 * Product Team sees this read-only; only SuperUser can edit, matching the server-side
 * rule (the API forbids tier writes for anyone else, so hiding the controls keeps the
 * UI honest rather than being the actual guard).
 */
function TierManagement({ user, onChanged }) {
  const canEdit = isSuperUser(user);

  const [tiers, setTiers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [tierForm, setTierForm] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingUnassign, setPendingUnassign] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [tierResult, assignmentResult] = await Promise.all([
        usageService.getTiers(true),
        usageService.getClientTiers(),
      ]);

      // .data because api/http.js has no response interceptor.
      setTiers(tierResult.data || []);
      setAssignments(assignmentResult.data || []);
    } catch (err) {
      setError(err?.response?.data || err?.message || "Unable to load tier configuration.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Empty string means "not set" for the nullable numeric fields, not zero. */
  const toNullableNumber = (value) =>
    value === "" || value === null || value === undefined ? null : Number(value);

  const saveTier = async () => {
    setError(null);

    try {
      const payload = {
        tierId: tierForm.tierId,
        tierName: tierForm.tierName,
        includedUnits: Number(tierForm.includedUnits) || 0,
        warnPercent: Number(tierForm.warnPercent) || 80,
        monthlyPrice: toNullableNumber(tierForm.monthlyPrice),
        overagePerUnit: toNullableNumber(tierForm.overagePerUnit),
        sortOrder: Number(tierForm.sortOrder) || 0,
        active: tierForm.active,
      };

      if (payload.tierId) {
        await usageService.updateTier(payload);
      } else {
        await usageService.createTier(payload);
      }

      setTierForm(null);
      setNotice("Tier saved.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data || err?.message || "Unable to save the tier.");
    }
  };

  const removeTier = async () => {
    setError(null);

    try {
      await usageService.deleteTier(pendingDelete.tierId);
      setPendingDelete(null);
      setNotice("Tier deleted.");
      await load();
      onChanged?.();
    } catch (err) {
      // The API refuses to delete a tier that still has clients on it, and returns the
      // reason — surface it rather than a generic failure.
      setError(err?.response?.data || err?.message || "Unable to delete the tier.");
      setPendingDelete(null);
    }
  };

  const saveAssignment = async () => {
    setError(null);

    try {
      await usageService.assignClientTier({
        clientId: assignmentForm.clientId.trim(),
        tierId: Number(assignmentForm.tierId),
        // Always sent. The API upserts and falls back to today when this is absent, so
        // omitting it silently reset the tier start date on every edit — and with one
        // row per client there is no history to recover the original from.
        effectiveFrom: assignmentForm.effectiveFrom || null,
        overrideLimit: toNullableNumber(assignmentForm.overrideLimit),
        notes: assignmentForm.notes || null,
      });

      setAssignmentForm(null);
      setNotice("Client tier assigned.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data || err?.message || "Unable to assign the tier.");
    }
  };

  const removeAssignment = async () => {
    setError(null);

    try {
      await usageService.removeClientTier(pendingUnassign.clientId);
      setPendingUnassign(null);
      setNotice("Assignment removed.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data || err?.message || "Unable to remove the assignment.");
      setPendingUnassign(null);
    }
  };

  return (
    <>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{String(error)}</Alert>}
      {notice && <Alert variant="success" onClose={() => setNotice(null)} dismissible>{notice}</Alert>}

      {!canEdit && (
        <Alert variant="info">
          Tier configuration is read-only for your role. A Super User can make changes.
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Tier ladder</span>
          {canEdit && (
            <Button size="sm" onClick={() => setTierForm({ ...emptyTier })}>
              <FaPlus className="me-1" /> New tier
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table striped hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-nowrap">Tier</th>
                  <th className="text-end text-nowrap">Included units</th>
                  <th className="text-end text-nowrap">Warns at</th>
                  <th className="text-end text-nowrap">Monthly price</th>
                  <th className="text-end text-nowrap">Overage / unit</th>
                  <th className="text-end text-nowrap">Clients</th>
                  <th className="text-nowrap">Status</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => (
                  <tr key={tier.tierId}>
                    <td className="fw-semibold">{tier.tierName}</td>
                    <td className="text-end" title={formatUnitsExact(tier.includedUnits)}>
                      {formatUnits(tier.includedUnits)}
                    </td>
                    <td className="text-end">{tier.warnPercent}%</td>
                    <td className="text-end">
                      {tier.monthlyPrice === null || tier.monthlyPrice === undefined
                        ? "—"
                        : `$${Number(tier.monthlyPrice).toFixed(2)}`}
                    </td>
                    <td className="text-end">
                      {tier.overagePerUnit === null || tier.overagePerUnit === undefined
                        ? "—"
                        : `$${Number(tier.overagePerUnit).toFixed(6)}`}
                    </td>
                    <td className="text-end">{tier.assignedClientCount}</td>
                    <td>
                      <Badge bg={tier.active ? "success" : "secondary"}>
                        {tier.active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    {canEdit && (
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          onClick={() =>
                            setTierForm({
                              ...tier,
                              monthlyPrice: tier.monthlyPrice ?? "",
                              overagePerUnit: tier.overagePerUnit ?? "",
                            })
                          }
                        >
                          <FaPencilAlt />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setPendingDelete(tier)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && tiers.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 8 : 7} className="text-center text-muted py-4">
                      {canEdit
                        ? 'No tiers defined yet. Use "New tier" to add the first one.'
                        : "No tiers defined yet. A Super User can add them."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Client assignments</span>
          {canEdit && (
            <Button size="sm" onClick={() => setAssignmentForm({ ...emptyAssignment })}>
              <FaPlus className="me-1" /> Assign client
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table striped hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-nowrap">Client</th>
                  <th className="text-nowrap">Tier</th>
                  <th className="text-end text-nowrap">Effective limit</th>
                  <th className="text-nowrap">Effective from</th>
                  <th className="text-nowrap">Notes</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.clientId}>
                    <td>
                      <div className="fw-semibold">{assignment.clientName}</div>
                      <div className="text-muted small">{assignment.clientId}</div>
                    </td>
                    <td>{assignment.tierName}</td>
                    <td className="text-end" title={formatUnitsExact(assignment.effectiveLimit)}>
                      {formatUnits(assignment.effectiveLimit)}
                      {assignment.overrideLimit !== null && assignment.overrideLimit !== undefined && (
                        <Badge bg="info" className="ms-2">
                          Override
                        </Badge>
                      )}
                    </td>
                    <td>{new Date(assignment.effectiveFrom).toLocaleDateString()}</td>
                    <td className="small text-muted">{assignment.notes || "—"}</td>
                    {canEdit && (
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          onClick={() =>
                            setAssignmentForm({
                              clientId: assignment.clientId,
                              tierId: assignment.tierId,
                              effectiveFrom: toDateInputValue(assignment.effectiveFrom),
                              overrideLimit: assignment.overrideLimit ?? "",
                              notes: assignment.notes ?? "",
                            })
                          }
                        >
                          <FaPencilAlt />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setPendingUnassign(assignment)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && assignments.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 6 : 5} className="text-center text-muted py-4">
                      No clients assigned to a tier yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Tier editor */}
      <Modal show={Boolean(tierForm)} onHide={() => setTierForm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{tierForm?.tierId ? "Edit tier" : "New tier"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tierForm && (
            <Form>
              <Form.Group className="mb-3" controlId="tierName">
                <Form.Label>Tier name</Form.Label>
                <Form.Control
                  value={tierForm.tierName}
                  onChange={(e) => setTierForm({ ...tierForm, tierName: e.target.value })}
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="includedUnits">
                    <Form.Label>Included units / month</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={tierForm.includedUnits}
                      onChange={(e) => setTierForm({ ...tierForm, includedUnits: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="warnPercent">
                    <Form.Label>Warn at (%)</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={100}
                      value={tierForm.warnPercent}
                      onChange={(e) => setTierForm({ ...tierForm, warnPercent: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="monthlyPrice">
                    <Form.Label>Monthly price</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="Optional"
                      value={tierForm.monthlyPrice}
                      onChange={(e) => setTierForm({ ...tierForm, monthlyPrice: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="overagePerUnit">
                    <Form.Label>Overage per unit</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step="0.000001"
                      placeholder="Optional"
                      value={tierForm.overagePerUnit}
                      onChange={(e) => setTierForm({ ...tierForm, overagePerUnit: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="sortOrder">
                    <Form.Label>Sort order</Form.Label>
                    <Form.Control
                      type="number"
                      value={tierForm.sortOrder}
                      onChange={(e) => setTierForm({ ...tierForm, sortOrder: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    label="Active"
                    checked={tierForm.active}
                    onChange={(e) => setTierForm({ ...tierForm, active: e.target.checked })}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTierForm(null)}>
            Cancel
          </Button>
          <Button onClick={saveTier} disabled={!tierForm?.tierName?.trim()}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assignment editor */}
      <Modal show={Boolean(assignmentForm)} onHide={() => setAssignmentForm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign client tier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assignmentForm && (
            <Form>
              <Form.Group className="mb-3" controlId="assignClientId">
                <Form.Label>Client id</Form.Label>
                <Form.Control
                  placeholder="e.g. C459"
                  value={assignmentForm.clientId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, clientId: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Assigning an id that already has a tier replaces that assignment.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3" controlId="assignTierId">
                <Form.Label>Tier</Form.Label>
                <Form.Select
                  value={assignmentForm.tierId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, tierId: e.target.value })}
                >
                  <option value={0}>Select a tier…</option>
                  {tiers.map((tier) => (
                    <option key={tier.tierId} value={tier.tierId}>
                      {tier.tierName} ({formatUnits(tier.includedUnits)} units)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="assignEffectiveFrom">
                <Form.Label>Effective from</Form.Label>
                <Form.Control
                  type="date"
                  value={assignmentForm.effectiveFrom}
                  onChange={(e) =>
                    setAssignmentForm({ ...assignmentForm, effectiveFrom: e.target.value })
                  }
                />
                <Form.Text className="text-muted">
                  When this tier took effect. Leave blank on a new assignment to use today.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3" controlId="assignOverrideLimit">
                <Form.Label>Override limit</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder="Leave blank to use the tier's included units"
                  value={assignmentForm.overrideLimit}
                  onChange={(e) =>
                    setAssignmentForm({ ...assignmentForm, overrideLimit: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="assignNotes">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  value={assignmentForm.notes}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAssignmentForm(null)}>
            Cancel
          </Button>
          <Button
            onClick={saveAssignment}
            disabled={!assignmentForm?.clientId?.trim() || !Number(assignmentForm?.tierId)}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={Boolean(pendingDelete)}
        title="Delete tier"
        question={`Delete "${pendingDelete?.tierName}"? Any clients still assigned to it must be reassigned first.`}
        yesButtonText="Delete"
        noButtonText="Cancel"
        handleConfirm={removeTier}
        handleClose={() => setPendingDelete(null)}
      />

      {/* Confirmed like the tier delete above: removing an assignment discards the
          client's negotiated override limit and notes with nothing to recover them from. */}
      <ConfirmModal
        show={Boolean(pendingUnassign)}
        title="Remove tier assignment"
        question={
          `Remove ${pendingUnassign?.clientName || pendingUnassign?.clientId} from the ` +
          `${pendingUnassign?.tierName} tier? Their override limit and notes will be lost, ` +
          `and they will show as having no tier.`
        }
        yesButtonText="Remove"
        noButtonText="Cancel"
        handleConfirm={removeAssignment}
        handleClose={() => setPendingUnassign(null)}
      />
    </>
  );
}

export default TierManagement;
